import { createStyles, withStyles, Tab, Tabs, useClientOptions, useUser } from '@paintscout/ui'
import type { MuiTheme as Theme, WithStyles } from '@paintscout/ui'
import type { QuoteProps } from '@paintscout/react-quote'
import { QuoteContext } from '@paintscout/react-quote'

import React, { useContext, useMemo } from 'react'

import { getFeature, getItems, getObjectLabels } from '@paintscout/util/builder'

const styles = (theme: Theme) =>
  createStyles({
    root: {},
    tabs: {
      '@media print': {
        display: 'none'
      },
      paddingLeft: 0,
      marginBottom: theme.spacing(0),
      [theme.breakpoints.down('xs')]: {
        '& button': {
          marginRight: theme.spacing(1)
        }
      }
    },
    tab: {
      padding: theme.spacing(1, 0)
    },
    tabDivider: {
      position: 'relative',
      display: 'flex',
      flexDirection: 'row',
      opacity: 1,
      '& span': {
        height: '10px',
        width: '2px',
        background: theme.paintscout.palette.brand.Neutral.P60
      }
    },
    tabsWrapper: {
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'row',
      gap: theme.spacing(2.5)
    }
  })

export interface EditableQuoteTabsProps extends WithStyles<typeof styles> {
  selectedTabValue: string
  onTabChange: (tabValue: QuoteProps['view']) => void
}

function EditableQuoteTabs({ classes, selectedTabValue, onTabChange }: EditableQuoteTabsProps) {
  const { quote } = useContext(QuoteContext)
  const { preferences, isSuperadmin, isSupport, isCrew } = useUser()
  const { options } = useClientOptions()
  const presentationsFeature =
    getFeature({
      options,
      preferences,
      path: quote.is_invoice ? 'presentation.presentationsOnInvoices' : 'presentation.enabled'
    }) ||
    isSuperadmin ||
    isSupport

  const objectLabels = getObjectLabels({ options, invoice: quote.is_invoice })

  const view = quote.defaultView === 'area' || quote.defaultView === 'substrate' ? quote.defaultView : 'area'
  const archivedItems = useMemo(
    () =>
      getItems({
        quote,
        section: 'archived',
        view,
        consumer: 'customer',
        options
      }),
    [quote, view, options]
  )
  const archivedLabel = archivedItems.length ? `Archived (${archivedItems.length})` : 'Archived'

  return (
    <Tabs
      variant="scrollable"
      classes={{ root: classes.tabs, flexContainer: classes.tabsWrapper }}
      value={selectedTabValue === 'product-order-form' ? false : selectedTabValue}
      onChange={handleTabChange}
    >
      <Tab className={classes.tab} label={objectLabels.quote.value} value="quote" />
      {presentationsFeature && !isCrew && (
        <Tab className={`${classes.tab} tour-quote-presentation-tab`} label={'Presentation'} value="presentation" />
      )}
      <Tab
        className={`${classes.tab} tour-quote-work-order-tab`}
        label={objectLabels.workOrder.value}
        value="work-order"
      />
      <Tab disabled classes={{ root: classes.tabDivider }} />
      <Tab className={classes.tab} label={archivedLabel} value="archived-items" />
      <Tab className={classes.tab} label={'Notes'} value="notes" />
    </Tabs>
  )

  function handleTabChange(event: any, value: QuoteProps['view']) {
    if (onTabChange) {
      onTabChange(value)
    }
  }
}

export default withStyles(styles)(EditableQuoteTabs)
